import React, { forwardRef } from 'react';
import clsx from 'clsx';

import { ScrollArea } from './ScrollArea';

// Types for Radix UI compatibility
type ResponsiveSize = '1' | '2' | '3';
type ResponsiveVariant = 'surface' | 'ghost';
type ResponsiveJustify = 'start' | 'center' | 'end';
type ResponsiveAlign = 'start' | 'center' | 'end' | 'baseline';

// Base Table Container
export interface TableRootProps {
  children: React.ReactNode;
  columns: string; // CSS grid template columns - required
  className?: string;
  style?: React.CSSProperties;
  scrollType?: 'auto' | 'always' | 'scroll' | 'hover';
  // Radix UI compatible props
  size?: ResponsiveSize;
  variant?: ResponsiveVariant;
  withDividers?: boolean;
  noScrollArea?: boolean;
}

function TableRoot({
  children,
  columns,
  className,
  style,
  scrollType = 'auto',
  size = '2',
  withDividers = false,
  variant = 'ghost',
  noScrollArea = false,
}: TableRootProps) {
  const tableStyle = {
    '--table-columns': columns,
    ...style,
  } as React.CSSProperties;

  if (noScrollArea) {
    return (
      <div
        className={clsx(
          'rt-Table',
          `rt-r-size-${size}`,
          `rt-variant-${variant}`,
          'table-container',
          withDividers && 'table--with-dividers',
          className
        )}
        style={tableStyle}
      >
        <div className="table">{children}</div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'rt-Table',
        `rt-r-size-${size}`,
        `rt-variant-${variant}`,
        'table-container',
        withDividers && 'table--with-dividers',
        className
      )}
      style={tableStyle}
    >
      <ScrollArea
        type={scrollType}
        style={{ height: '100%' }}
        className="table-scroll"
      >
        <div className="table">{children}</div>
      </ScrollArea>
    </div>
  );
}

// Table Header
export interface TableHeaderProps {
  children: React.ReactNode;
  className?: string;
  sticky?: boolean;
}

function TableHeader({ children, className, sticky = true }: TableHeaderProps) {
  return (
    <div
      className={clsx(
        'rt-TableHeader',
        'table__header',
        {
          'table__header--sticky': sticky,
        },
        className
      )}
    >
      {children}
    </div>
  );
}

// Table Body
export interface TableBodyProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

function TableBody({ children, className, style }: TableBodyProps) {
  return (
    <div
      className={clsx('rt-TableBody', 'table__body', className)}
      style={style}
    >
      {children}
    </div>
  );
}

// Table Row
export type TableRowProps = React.HTMLAttributes<HTMLDivElement> & {
  // Radix UI compatible props
  align?: ResponsiveAlign;
};

const TableRow = forwardRef<HTMLDivElement, TableRowProps>(
  ({ children, className, onClick, align, style, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'rt-TableRow',
          'table__row',
          {
            'table__row--clickable': !!onClick,
            [`rt-r-ai-${align}`]: align,
          },
          className
        )}
        onClick={onClick}
        style={style}
        {...props}
      >
        {children}
      </div>
    );
  }
);

// Table Cell
export interface TableCellProps {
  children: React.ReactNode;
  className?: string;
  stickyFirst?: boolean;
  style?: React.CSSProperties;
  // Radix UI compatible props
  justify?: ResponsiveJustify;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
}

function TableCell({
  children,
  className,
  stickyFirst = false,
  style,
  justify,
  width,
  minWidth,
  maxWidth,
}: TableCellProps) {
  const cellStyle = {
    width,
    minWidth,
    maxWidth,
    ...style,
  } as React.CSSProperties;

  return (
    <div
      className={clsx(
        'rt-TableCell',
        'table__cell',
        {
          'table__cell--sticky-first': stickyFirst,
          [`rt-r-jc-${justify}`]: justify,
        },
        className
      )}
      style={cellStyle}
    >
      {children}
    </div>
  );
}

// Table Header Cell
export interface TableHeaderCellProps {
  children: React.ReactNode;
  className?: string;
  stickyFirst?: boolean;
  style?: React.CSSProperties;
  // Radix UI compatible props
  justify?: ResponsiveJustify;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
}

function TableHeaderCell({
  children,
  className,
  stickyFirst = false,
  style,
  justify,
  width,
  minWidth,
  maxWidth,
}: TableHeaderCellProps) {
  const cellStyle = {
    width,
    minWidth,
    maxWidth,
    ...style,
  } as React.CSSProperties;

  return (
    <div
      className={clsx(
        'rt-TableColumnHeaderCell',
        'table__header-cell',
        {
          'table__header-cell--sticky-first': stickyFirst,
          [`rt-r-jc-${justify}`]: justify,
        },
        className
      )}
      style={cellStyle}
    >
      {children}
    </div>
  );
}

// Export all table components
export {
  TableRoot,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
};
